// Виджеты на холсте: разметка карточки, загрузка данных, отрисовка, период, меню.

import type { DataQueryResponse, WidgetDefinition } from '../types';
import { isAnonymous } from '../auth';
import { api, localizeWithComparison } from '../api';
import { isSnapshotVisualization, periodsInData } from '../data-utils';
import { setVisualContext } from '../visual-utils';
import { t } from '../i18n';

import type { GridElementLike } from './canvas';
import { clone, createClientId } from './model';
import { controls, element } from './controls';
import { currentLocale } from './locale';
import { currentTheme, resolvedThemeMode } from './theming';
import { desktopFixedCanvas, lastAppliedGridColumns, observeWidgetVisual, responsiveLayoutFor, scheduleGridMetricsUpdate, scheduleVisualResize, setLayoutEditing, syncLayoutsFromGrid, unobserveWidgetVisual, updateDashboardCanvasScale, updateWidgetSizeBadge, setLastAppliedGridColumns } from './canvas';
import { dimensionSelectionMarkup, dimensionSelectionRequired } from './dimension-selection';
import { errorMessage, markDirty, russianCount, setStatus, updateWidgetCounter } from './status';
import { escapeHtml, icon, refreshIcons, removeEmptyKpiIcons } from './dom';
import { grid } from './grid';
import { isMapVisualization, minimumWidgetHeight } from './widget-form';
import { loadChartModule, loadMapModule, loadedChartModule, loadedMapModule } from './visual-loader';
import { mergeKnownTerritories } from './territories';
import { resetWidgetForm } from './widget-editor';
import { state } from './state';
import { syncSelectedWidgetClass, updateInspectorState } from './workspace';
import { activeScopeTerritoryId, scopedQuery } from './scope-filter';
import { dataTranslation, tr } from '../ui-i18n';

// В state.dataCache лежит ответ бэкенда как есть (по-русски, см. loadWidgetData);
// на язык интерфейса его переводит effectiveData при каждой отрисовке, поэтому
// смена языка перерисовывает виджеты из кэша, без запросов. Территориальный
// фильтр дашборда меняет сам запрос (scope-filter.ts) — строки здесь не режутся.
export function effectiveData(_widget: WidgetDefinition, rawData: DataQueryResponse): DataQueryResponse {
  return localizeWithComparison(rawData, currentLocale());
}

export function availablePeriods(_widget: WidgetDefinition, data: DataQueryResponse): string[] {
  return periodsInData(data);
}

export function resolveWidgetPeriod(widget: WidgetDefinition, data: DataQueryResponse): string | null {
  const periods = availablePeriods(widget, data);
  const period = widget.time.activePeriod && periods.includes(widget.time.activePeriod)
    ? widget.time.activePeriod
    : periods.at(-1) ?? null;
  widget.time.activePeriod = period;
  return period;
}

/** Заголовок виджета на языке интерфейса: titleEn, перевод title по словарю или сам title. */
export function displayWidgetTitle(widget: WidgetDefinition): string {
  if (currentLocale() !== 'en') return widget.title;
  return widget.titleEn?.trim() || dataTranslation(widget.title) || widget.title;
}

/** Виджет с заголовком на языке интерфейса — для отрисовки (графики, карты, выгрузка). */
export function displayedWidget(widget: WidgetDefinition): WidgetDefinition {
  const title = displayWidgetTitle(widget);
  return title === widget.title ? widget : { ...widget, title };
}

function updateWidgetTitle(id: string, widget: WidgetDefinition): void {
  const node = controls.gridRoot.querySelector<HTMLElement>(`.grid-stack-item[gs-id="${CSS.escape(id)}"] .widget-title`);
  if (!node) return;
  const title = displayWidgetTitle(widget);
  node.textContent = title;
  node.title = title;
}

export function widgetMarkup(widget: WidgetDefinition): string {
  const locale = currentLocale();
  const title = displayWidgetTitle(widget);
  return `
    <header class="widget-header">
      <span class="widget-drag-grip" aria-hidden="true">${icon('grip-vertical')}</span>
      <div class="widget-title-block">
        <div class="widget-title" data-i18n-skip title="${escapeHtml(title)}">${escapeHtml(title)}</div>
      </div>
      <div class="widget-period-control" data-period-control hidden>
        <button type="button" class="period-step" data-action="period-prev" data-tooltip="${escapeHtml(t(locale, 'previousPeriod'))}" aria-label="${escapeHtml(t(locale, 'previousPeriod'))}">${icon('chevron-left')}</button>
        <select data-period-select aria-label="${escapeHtml(t(locale, 'periodDisplay'))}"></select>
        <button type="button" class="period-step" data-action="period-next" data-tooltip="${escapeHtml(t(locale, 'nextPeriod'))}" aria-label="${escapeHtml(t(locale, 'nextPeriod'))}">${icon('chevron-right')}</button>
      </div>
      <div class="widget-actions">
        <button type="button" class="widget-action" data-action="widget-menu" data-tooltip="${escapeHtml(t(locale, 'widgetActions'))}" aria-label="${escapeHtml(t(locale, 'widgetActions'))}" aria-expanded="false">${icon('more-vertical')}</button>
        <div class="widget-menu-popover" data-widget-menu hidden>
          <button type="button" data-action="refresh">${icon('refresh-cw')}<span>${escapeHtml(t(locale, 'refreshData'))}</span></button>
          <button type="button" data-action="duplicate">${icon('copy')}<span>${escapeHtml(t(locale, 'duplicate'))}</span></button>
          <button type="button" data-action="layout-menu" aria-expanded="false">${icon('move')}<span>${escapeHtml(t(locale, 'layoutCommands'))}</span></button>
          <button type="button" data-action="edit">${icon('sliders-horizontal')}<span>${escapeHtml(t(locale, 'edit'))}</span></button>
          <button type="button" class="danger" data-action="delete">${icon('trash-2')}<span>${escapeHtml(t(locale, 'delete'))}</span></button>
        </div>
      </div>
      <div class="widget-layout-popover" data-layout-popover hidden>
        <strong>${escapeHtml(t(locale, 'position'))}</strong>
        <div class="layout-command-grid" role="group" aria-label="${escapeHtml(t(locale, 'position'))}">
          <button type="button" data-layout-action="up" aria-label="Переместить вверх">${icon('arrow-up')}</button>
          <button type="button" data-layout-action="down" aria-label="Переместить вниз">${icon('arrow-down')}</button>
          <button type="button" data-layout-action="left" aria-label="Переместить влево">${icon('arrow-left')}</button>
          <button type="button" data-layout-action="right" aria-label="Переместить вправо">${icon('arrow-right')}</button>
        </div>
        <strong>${escapeHtml(t(locale, 'sizeByGrid'))}</strong>
        <div class="layout-size-commands" role="group" aria-label="${escapeHtml(t(locale, 'sizeByGrid'))}">
          <button type="button" data-layout-action="narrower">${escapeHtml(t(locale, 'narrower'))}</button>
          <button type="button" data-layout-action="wider">${escapeHtml(t(locale, 'wider'))}</button>
          <button type="button" data-layout-action="shorter">${escapeHtml(t(locale, 'shorter'))}</button>
          <button type="button" data-layout-action="taller">${escapeHtml(t(locale, 'taller'))}</button>
        </div>
      </div>
    </header>
    <div id="visual-${escapeHtml(widget.id)}" class="widget-visual"><div class="visual-loading">Подготовка…</div></div>
    <footer id="meta-${escapeHtml(widget.id)}" class="widget-meta"></footer>`;
}

export function addWidgetToGrid(widget: WidgetDefinition, displayLayout = widget.layout): void {
  const minH = minimumWidgetHeight(widget.visualization);
  const item = grid.addWidget({
    id: widget.id,
    x: displayLayout.x,
    y: displayLayout.y,
    w: displayLayout.w,
    h: Math.max(displayLayout.h, minH),
    minW: 3,
    minH,
    content: widgetMarkup(widget),
  });
  refreshIcons(item);
  removeEmptyKpiIcons(item);
  updateWidgetSizeBadge(item as GridElementLike);
  observeWidgetVisual(widget.id);
  syncSelectedWidgetClass();
}

export function renderDashboardGrid(): void {
  state.renderEpoch += 1;
  state.suppressGridEvents = true;
  state.renderVersions.forEach((_version, id) => {
    unobserveWidgetVisual(id);
    disposeWidgetVisual(id);
  });
  state.renderVersions.clear();
  state.dataCache.clear();
  state.visualCompactModes.clear();
  grid.removeAll();
  grid.batchUpdate();
  updateDashboardCanvasScale();
  if (desktopFixedCanvas() && grid.getColumn() !== 12) grid.column(12, 'none');
  setLastAppliedGridColumns(desktopFixedCanvas() ? 12 : grid.getColumn());
  const cursor = { x: 0, y: 0, rowHeight: 0 };
  state.definition.widgets.forEach((widget) => {
    addWidgetToGrid(widget, responsiveLayoutFor(widget, lastAppliedGridColumns, cursor));
  });
  grid.batchUpdate(false);
  state.suppressGridEvents = false;
  updateWidgetCounter();
  scheduleGridMetricsUpdate();
  if (state.editingWidgetId && !findWidget(state.editingWidgetId)) state.editingWidgetId = null;
  updateInspectorState();
  requestAnimationFrame(() => {
    state.definition.widgets.forEach((widget) => void renderWidget(widget.id));
  });
}

export function findWidget(id: string): WidgetDefinition | undefined {
  return state.definition.widgets.find((widget) => widget.id === id);
}

export function visualContainer(id: string): HTMLElement | null {
  return document.getElementById(`visual-${id}`);
}

export function disposeWidgetVisual(id: string): void {
  loadedChartModule?.disposeChart(`visual-${id}`);
  loadedMapModule?.disposeMap(`visual-${id}`);
}

export function resizeWidgetVisual(id: string): void {
  loadedChartModule?.resizeChart(`visual-${id}`);
  loadedMapModule?.resizeMap(`visual-${id}`);
}

export function cacheLabel(status: string): string {
  const labels: Record<string, string> = {
    local: 'локальный набор',
    hit: 'сохранённые данные',
    'hit-after-wait': 'сохранённые данные',
    'miss-fetched': 'обновлено из источника',
    miss: 'загружено из источника',
    'stale-if-error': 'показана сохранённая версия',
  };
  return labels[status] ?? status;
}

export function userSourceLabel(data: DataQueryResponse): string {
  if (data.meta.source_type === 'bdmo') return 'Муниципальная статистика';
  if (data.meta.source_type === 'emiss') return 'Региональная статистика';
  if (data.meta.source_type === 'worldbank') return 'Международная статистика';
  if (data.meta.source_type === 'settlements') return 'Населённые пункты';
  return data.meta.source_name;
}

export function dataStatusLabel(data: DataQueryResponse, problematic: number): string {
  if (data.meta.source_type === 'bdmo') return problematic ? 'есть замечания' : 'проверено';
  if (data.meta.cache_status === 'stale-if-error') return 'показана сохранённая версия';
  return 'обновлено';
}

export function updatePeriodControl(id: string, widget: WidgetDefinition, data: DataQueryResponse): void {
  const card = controls.gridRoot.querySelector<HTMLElement>(`[data-widget-id="${CSS.escape(id)}"]`);
  const periodControl = card?.querySelector<HTMLElement>('[data-period-control]');
  const select = periodControl?.querySelector<HTMLSelectElement>('[data-period-select]');
  if (!periodControl || !select) return;
  const periods = availablePeriods(widget, data);
  const active = resolveWidgetPeriod(widget, data);
  const show = isSnapshotVisualization(widget.visualization) && widget.time.showControl && periods.length > 0;
  periodControl.hidden = !show;
  if (!show) return;
  select.innerHTML = periods
    .map((period) => `<option value="${escapeHtml(period)}">${escapeHtml(period)}</option>`)
    .join('');
  select.value = active ?? periods.at(-1) ?? '';
  select.disabled = periods.length <= 1;
  const activeIndex = periods.indexOf(select.value);
  const previous = periodControl.querySelector<HTMLButtonElement>('[data-action="period-prev"]');
  const next = periodControl.querySelector<HTMLButtonElement>('[data-action="period-next"]');
  if (previous) previous.disabled = activeIndex <= 0;
  if (next) next.disabled = activeIndex < 0 || activeIndex >= periods.length - 1;
  periodControl.classList.toggle('is-single-period', periods.length <= 1);
}

export function displayedRows(widget: WidgetDefinition, data: DataQueryResponse): number {
  if (!isSnapshotVisualization(widget.visualization)) return data.rows.length;
  const period = resolveWidgetPeriod(widget, data);
  return period ? data.rows.filter((row) => row.period === period).length : 0;
}

export function updateWidgetMeta(id: string, widget: WidgetDefinition, data: DataQueryResponse): void {
  const meta = document.getElementById(`meta-${id}`);
  if (!meta) return;
  const period = isSnapshotVisualization(widget.visualization) ? resolveWidgetPeriod(widget, data) : null;
  const problematic = ['conflict', 'anomaly', 'missing']
    .reduce((sum, key) => sum + Number(data.meta.quality?.[key] ?? 0), 0);
  const observations = russianCount(displayedRows(widget, data), 'наблюдение', 'наблюдения', 'наблюдений');
  const statusLabel = tr(dataStatusLabel(data, problematic));
  const parts = [
    observations,
    period ? period : null,
    statusLabel,
  ].filter(Boolean);
  // Строка собирается из частей — каждая переводится сама (ui-i18n.ts).
  const fullMeta = [
    tr(userSourceLabel(data)),
    observations,
    period ? tr('период {0}', period) : null,
    tr('режим: {0}', tr(cacheLabel(data.meta.cache_status))),
    data.meta.query_ms != null ? tr('{0} мс', Math.round(data.meta.query_ms)) : null,
    data.meta.source_type === 'bdmo' ? (problematic ? tr('контроль качества: {0} замеч.', problematic) : tr('контроль качества: пройден')) : null,
  ].filter(Boolean).join(' · ');
  meta.innerHTML = `
    <span title="${escapeHtml(fullMeta)}">${escapeHtml(parts.join(' · '))}</span>
  `;
}

export async function renderWidgetFromData(id: string, rawData: DataQueryResponse, preserveMap = false): Promise<void> {
  const widget = findWidget(id);
  const container = visualContainer(id);
  if (!widget || !container) return;
  setVisualContext(currentTheme(), currentLocale(), resolvedThemeMode());
  const data = effectiveData(widget, rawData);
  resolveWidgetPeriod(widget, data);
  updatePeriodControl(id, widget, data);
  if (!data.rows.length || displayedRows(widget, data) === 0) {
    disposeWidgetVisual(id);
    container.innerHTML = '<div class="visual-message">Для выбранного фильтра и периода нет данных.</div>';
    updateWidgetMeta(id, widget, data);
    return;
  }
  updateWidgetTitle(id, widget);
  const shown = displayedWidget(widget);
  if (isMapVisualization(widget.visualization)) {
    const mapModule = await loadMapModule();
    const updated = preserveMap && mapModule.updateMapPeriod(container, shown, data);
    if (!updated) await mapModule.renderMap(container, shown, data);
  } else {
    const chartModule = await loadChartModule();
    chartModule.renderChart(container, shown, data);
    refreshIcons(container);
    removeEmptyKpiIcons(container);
  }
  state.visualCompactModes.set(id, container.clientWidth < 520);
  updateWidgetMeta(id, widget, data);
  observeWidgetVisual(id);
  scheduleVisualResize(id);
}

export async function rerenderCachedWidgets(preserveMaps = true, loadMissing = false): Promise<void> {
  await Promise.all(
    state.definition.widgets.map(async (widget) => {
      const data = state.dataCache.get(widget.id);
      if (data) await renderWidgetFromData(widget.id, data, preserveMaps);
      // Виджет без данных в кэше (ещё грузится или с ошибкой) — загрузить.
      else if (loadMissing) await renderWidget(widget.id, false);
    }),
  );
}

export async function loadWidgetData(widget: WidgetDefinition, forceRefresh: boolean): Promise<DataQueryResponse> {
  // Аноним (вход включён, но не выполнен) запросов к данным не делает: бэкенд
  // исполняет запросы виджетов сохранённого общего дашборда сам, включая
  // показатели сравнения и переписывая территории под фильтр дашборда.
  const savedDashboardId = state.currentTemplateId ?? state.currentDashboardId;
  if (isAnonymous()) {
    if (!savedDashboardId) throw new Error(tr('Войдите, чтобы строить собственные виджеты.'));
    return api.dashboardWidgetData(savedDashboardId, widget.id, 'ru', activeScopeTerritoryId());
  }
  // Территориальный фильтр дашборда: территории запроса — выбранной территории.
  const query = await scopedQuery(widget);
  // Без перевода: кэш хранит исходный ответ, переводит effectiveData.
  const data = await api.queryData(query, forceRefresh, 'ru');
  const compareIds = [...new Set(query.compareIndicatorIds ?? [])]
    .filter((indicatorId) => indicatorId && indicatorId !== query.indicatorId)
    .slice(0, 4);
  if (!compareIds.length) return data;
  const responses = await Promise.all(compareIds.map((indicatorId) => api.queryData({
    ...query,
    indicatorId,
    compareIndicatorIds: [],
  }, forceRefresh, 'ru')));
  return {
    ...data,
    meta: {
      ...data.meta,
      source_details: {
        ...(data.meta.source_details ?? {}),
        comparison: { responses },
      },
    },
  };
}

export async function renderWidget(id: string, forceRefresh = false): Promise<void> {
  const widget = findWidget(id);
  const container = visualContainer(id);
  if (!widget || !container) return;
  const epoch = state.renderEpoch;
  const version = (state.renderVersions.get(id) ?? 0) + 1;
  state.renderVersions.set(id, version);
  disposeWidgetVisual(id);
  updateWidgetTitle(id, widget);
  container.innerHTML = '<div class="visual-loading"><span class="spinner"></span>Загрузка данных…</div>';
  const meta = document.getElementById(`meta-${id}`);
  if (meta) meta.textContent = 'Выполняется запрос';
  try {
    const data = await loadWidgetData(widget, forceRefresh);
    if (state.renderEpoch !== epoch || state.renderVersions.get(id) !== version || !findWidget(id)) return;
    // Из строк — только территории, которых нет в каталоге: имена в строках
    // исходные (по-русски) и без name_en, каталожные записи они бы затёрли.
    const knownTerritoryIds = new Set(state.territories.map((territory) => territory.id));
    mergeKnownTerritories(data.rows.filter((row) => !knownTerritoryIds.has(row.territory_id)).map((row) => ({
      id: row.territory_id,
      name: row.territory_name,
      level: row.territory_level,
      parent_id: null,
      federal_district_id: null,
      region_id: null,
      oktmo: row.oktmo ?? null,
      geo_layer_id: data.meta.geo_layer_id,
      geo_feature_id: row.oktmo ?? null,
      centroid_lon: null,
      centroid_lat: null,
      source_type: data.meta.source_type,
      selectable: true,
      metadata: {},
    })));
    state.dataCache.set(id, data);
    await renderWidgetFromData(id, data, false);
    if (state.renderEpoch !== epoch || state.renderVersions.get(id) !== version || !findWidget(id)) return;
  } catch (error) {
    if (state.renderEpoch !== epoch || state.renderVersions.get(id) !== version) return;
    const selection = dimensionSelectionRequired(error);
    if (selection) {
      container.innerHTML = dimensionSelectionMarkup(selection, !state.viewMode);
      if (meta) meta.textContent = 'Нужно выбрать значение разреза';
      observeWidgetVisual(id);
      return;
    }
    container.innerHTML = `
      <div class="visual-error">
        <strong>Не удалось построить виджет</strong>
        <span>${escapeHtml(errorMessage(error))}</span>
        <button type="button" class="text-button" data-action="refresh">Повторить</button>
      </div>`;
    if (meta) meta.textContent = 'Ошибка запроса';
    observeWidgetVisual(id);
  }
}

export function removeWidget(id: string): void {
  const item = controls.gridRoot.querySelector<HTMLElement>(`.grid-stack-item[gs-id="${CSS.escape(id)}"]`)
    ?? controls.gridRoot.querySelector<HTMLElement>(`[data-widget-id="${CSS.escape(id)}"]`)?.closest('.grid-stack-item');
  unobserveWidgetVisual(id);
  disposeWidgetVisual(id);
  state.renderVersions.delete(id);
  state.dataCache.delete(id);
  state.visualCompactModes.delete(id);
  if (item) grid.removeWidget(item);
  state.definition.widgets = state.definition.widgets.filter((widget) => widget.id !== id);
  if (state.editingWidgetId === id) {
    state.inspectorVisualization = null;
    resetWidgetForm();
  }
  updateWidgetCounter();
  markDirty();
}

export function duplicateWidget(id: string): void {
  const source = findWidget(id);
  if (!source) return;
  syncLayoutsFromGrid();
  const duplicate = clone(source);
  duplicate.id = createClientId();
  duplicate.title = `${source.title} — копия`;
  duplicate.layout = {
    ...source.layout,
    x: Math.min(6, source.layout.x + 1),
    y: source.layout.y + 1,
  };
  state.definition.widgets.push(duplicate);
  addWidgetToGrid(duplicate);
  updateWidgetCounter();
  scheduleGridMetricsUpdate();
  markDirty();
  void renderWidget(duplicate.id);
}

export function replaceWidgetInGrid(widget: WidgetDefinition, refreshData = true): void {
  const item = controls.gridRoot.querySelector<HTMLElement>(`.grid-stack-item[gs-id="${CSS.escape(widget.id)}"]`);
  const content = item?.querySelector<HTMLElement>('.grid-stack-item-content');
  if (content) {
    unobserveWidgetVisual(widget.id);
    disposeWidgetVisual(widget.id);
    content.dataset.widgetId = widget.id;
    content.classList.add('widget-card');
    content.innerHTML = widgetMarkup(widget).trim();
    refreshIcons(content);
    observeWidgetVisual(widget.id);
    updateWidgetSizeBadge(item as GridElementLike);
  } else {
    addWidgetToGrid(widget);
  }
  syncSelectedWidgetClass();
  if (refreshData) {
    void renderWidget(widget.id);
  } else {
    const cached = state.dataCache.get(widget.id);
    if (cached) void renderWidgetFromData(widget.id, cached, false);
    else void renderWidget(widget.id);
  }
}

export async function refreshAllWidgets(): Promise<void> {
  const button = element<HTMLButtonElement>('#refresh-all');
  button.disabled = true;
  setStatus('Обновление всех виджетов…');
  try {
    await Promise.all(state.definition.widgets.map((widget) => renderWidget(widget.id, true)));
    setStatus('Все виджеты обновлены', 'success');
  } finally {
    button.disabled = false;
  }
}

export function widgetIdFromTarget(target: Element): string | null {
  const card = target.closest<HTMLElement>('[data-widget-id]');
  return card?.dataset.widgetId ?? null;
}

export function isWidgetControlTarget(target: Element): boolean {
  return Boolean(target.closest(
    'button, input, select, textarea, a, [data-action], [data-widget-menu], [data-layout-popover], .maplibregl-ctrl',
  ));
}

export async function setWidgetPeriod(id: string, period: string): Promise<void> {
  const widget = findWidget(id);
  const rawData = state.dataCache.get(id);
  if (!widget || !rawData) return;
  const data = effectiveData(widget, rawData);
  if (!availablePeriods(widget, data).includes(period)) return;
  widget.time.activePeriod = period;
  markDirty();
  await renderWidgetFromData(id, rawData, true);
  setStatus(`Период виджета изменён: ${period}`, 'success');
}

export async function stepWidgetPeriod(id: string, direction: -1 | 1): Promise<void> {
  const widget = findWidget(id);
  const rawData = state.dataCache.get(id);
  if (!widget || !rawData) return;
  const data = effectiveData(widget, rawData);
  const periods = availablePeriods(widget, data);
  const current = resolveWidgetPeriod(widget, data);
  const index = current ? periods.indexOf(current) : -1;
  const next = periods[index + direction];
  if (next) await setWidgetPeriod(id, next);
}

export function closeWidgetMenus(except?: HTMLElement): void {
  controls.gridRoot.querySelectorAll<HTMLElement>('[data-widget-menu]').forEach((menu) => {
    if (menu === except) return;
    menu.hidden = true;
    const button = menu.closest<HTMLElement>('.widget-actions')?.querySelector<HTMLElement>('[data-action="widget-menu"]');
    button?.setAttribute('aria-expanded', 'false');
  });
}

export function toggleWidgetMenu(id: string, button: HTMLElement): void {
  const card = button.closest<HTMLElement>('[data-widget-id]');
  const menu = card?.querySelector<HTMLElement>('[data-widget-menu]');
  if (!menu) return;
  const willOpen = menu.hidden;
  closeWidgetMenus(menu);
  controls.gridRoot.querySelectorAll<HTMLElement>('[data-layout-popover]').forEach((item) => {
    item.hidden = true;
  });
  menu.hidden = !willOpen;
  button.setAttribute('aria-expanded', String(willOpen));
  if (willOpen) menu.querySelector<HTMLButtonElement>('button')?.focus();
  const widget = findWidget(id);
  if (widget) setStatus(`Действия виджета: ${widget.title}`);
}

export function toggleLayoutPopover(id: string, button: HTMLElement): void {
  const card = button.closest<HTMLElement>('[data-widget-id]');
  const popover = card?.querySelector<HTMLElement>('[data-layout-popover]');
  if (!popover) return;
  closeWidgetMenus();
  controls.gridRoot.querySelectorAll<HTMLElement>('[data-layout-popover]').forEach((item) => {
    if (item !== popover) item.hidden = true;
  });
  const willOpen = popover.hidden;
  popover.hidden = !willOpen;
  button.setAttribute('aria-expanded', String(willOpen));
  if (willOpen) popover.querySelector<HTMLButtonElement>('button')?.focus();
  if (!state.layoutEditing) setLayoutEditing(true);
  const widget = findWidget(id);
  if (widget) setStatus(`Команды расположения: ${widget.title}`);
}
